import { apiClient } from './apiClient';

export class SummaryService {
  constructor() {
    this.BASE_URL = '/summaries';
  }

  /**
   * Get a well summary for a specific week
   * @param {string} wellCode - The well code
   * @param {string} date - ISO string date within the target week
   * @param {boolean} forceRefresh - Whether to regenerate the summary
   * @returns {Promise<Object>} The well summary data
   */
  async getWellWeeklySummary(wellCode, date, forceRefresh = false) {
    return apiClient.get(
      `${this.BASE_URL}/well/weekly?id=${encodeURIComponent(wellCode)}&date=${encodeURIComponent(date)}&forceRefresh=${forceRefresh}`
    );
  }

  /**
   * Get an operator weekly summary
   * @param {string} operatorEmail - The operator email
   * @param {string} date - ISO string date within the target week
   * @param {boolean} forceRefresh - Whether to regenerate the summary
   * @returns {Promise<Object>} The operator summary data
   */
  async getOperatorWeeklySummary(operatorEmail, date, forceRefresh = false) {
    return apiClient.get(
      `${this.BASE_URL}/operator/weekly?id=${encodeURIComponent(operatorEmail)}&date=${encodeURIComponent(date)}&forceRefresh=${forceRefresh}`
    );
  }

  /**
   * Generate insights for a specific summary
   * @param {string} entityType - 'well' or 'operator'
   * @param {string} frequency - 'weekly' or 'monthly'
   * @param {string} summaryId - The ID of the summary
   * @returns {Promise<Object>} The generated insights
   */
  async generateInsights(entityType, frequency, summaryId) {
    return apiClient.post(`${this.BASE_URL}/${entityType}/${frequency}/insights`, { summaryId });
  }

  /**
   * Compare two summaries
   * @param {Object} params - Comparison parameters
   * @returns {Promise<Object>} The comparison results
   */
  async compareSummaries(params) {
    return apiClient.post(`${this.BASE_URL}/compare`, params);
  }

  /**
   * Get summaries of spliced EOB pages for a patient
   * @param {string} patientId - The patient ID
   * @returns {Promise<Array>} List of spliced page summaries (from patient notes)
   */
  async getPatientSplicedSummaries(patientId) {
    const response = await apiClient.get(`/notes/patient/${patientId}`);
    const notes = Array.isArray(response?.data) ? response.data : (Array.isArray(response) ? response : []);
    return notes.filter(note => note.type === 'EOB' || note.metadata?.documentId);
  }
}

// Create a singleton instance
export const summaryService = new SummaryService();